/* Dedicated Apex 2.0 page — reuses <ApexCard /> as the primary control panel
   and wraps it in a proper topbar so it feels like a first-class feature,
   not a settings row. Mounted at /app/apex. */
import { useState } from "react";
import { Link } from "react-router-dom";
import { PhoneCall, ArrowRight, Sparkles, ListChecks, Loader2, Check, Copy } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import ApexCard from "@/components/ApexCard";

export default function ApexPage() {
  return (
    <div data-testid="apex-page">
      <div className="topbar">
        <div>
          <div className="crumb flex items-center gap-1.5">
            <PhoneCall size={11} /> AI Voice Lead Manager
          </div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight mt-1">
            Apex 2.0
          </h1>
          <p className="text-[var(--ink-2)] text-sm mt-1 max-w-2xl">
            Your AI receptionist for inbound seller calls. Answers 24/7 (or during
            hours you pick), qualifies the lead, captures property details, and
            optionally books a callback into your calendar — so you never miss a
            hot deal because you were in another call, driving, or asleep.
          </p>
        </div>

        <div className="ml-auto flex items-center gap-2">
          <Link
            to="/app/admin/apex-calls"
            className="btn-outline flex items-center gap-1.5"
            data-testid="apex-page-open-call-log"
          >
            <ListChecks size={13} /> Call log
          </Link>
        </div>
      </div>

      {/* Bind existing number — needed so Apex knows which account an inbound
          call belongs to. If skipped, calls hit "This number is not configured." */}
      <BindExistingNumberCard />

      {/* Setup checklist — surfaces the two things a first-time user must do. */}
      <div className="card mb-5 bg-gradient-to-br from-[var(--primary)]/5 to-white"
           data-testid="apex-page-checklist">
        <div className="label-eyebrow flex items-center gap-1.5 mb-3">
          <Sparkles size={11} /> Before your first call
        </div>
        <ol className="space-y-3 text-sm">
          <li className="grid grid-cols-[20px_1fr] gap-3 items-start">
            <span className="mt-[3px] w-5 h-5 rounded-full bg-[var(--primary)] text-[var(--brand-primary-dark)] font-black text-[11px] flex items-center justify-center leading-none">1</span>
            <span className="text-[var(--ink-2)] leading-relaxed">
              <b className="text-[var(--ink)]">Bind your Twilio number above</b>{" "}
              so Apex knows the call belongs to you.
            </span>
          </li>
          <li className="grid grid-cols-[20px_1fr] gap-3 items-start">
            <span className="mt-[3px] w-5 h-5 rounded-full bg-[var(--primary)] text-[var(--brand-primary-dark)] font-black text-[11px] flex items-center justify-center leading-none">2</span>
            <span className="text-[var(--ink-2)] leading-relaxed">
              <b className="text-[var(--ink)]">Point that number&apos;s Voice webhook at Apex.</b>{" "}
              In Twilio Console → Phone Numbers → your number → Voice
              Configuration: set the &quot;A CALL COMES IN&quot; webhook to{" "}
              <code className="bg-slate-100 text-[var(--ink)] px-1.5 py-0.5 rounded text-[11px] font-mono align-middle whitespace-nowrap">
                https://ispeedtoclose.com/api/apex/twiml/answer
              </code>{" "}
              <span className="text-[var(--ink-3)]">(POST)</span>.
            </span>
          </li>
          <li className="grid grid-cols-[20px_1fr] gap-3 items-start">
            <span className="mt-[3px] w-5 h-5 rounded-full bg-[var(--primary)] text-[var(--brand-primary-dark)] font-black text-[11px] flex items-center justify-center leading-none">3</span>
            <span className="text-[var(--ink-2)] leading-relaxed">
              <b className="text-[var(--ink)]">Flip the Apex toggle ON below</b> and
              pick a voice + greeting.
            </span>
          </li>
          <li className="grid grid-cols-[20px_1fr] gap-3 items-start">
            <span className="mt-[3px] w-5 h-5 rounded-full bg-[var(--primary)] text-[var(--brand-primary-dark)] font-black text-[11px] flex items-center justify-center leading-none">4</span>
            <span className="text-[var(--ink-2)] leading-relaxed">
              <b className="text-[var(--ink)]">Test it.</b> Call the number from
              another phone — Apex should pick up. Check the{" "}
              <Link to="/app/admin/apex-calls" className="underline text-[var(--primary-dark)]">
                Call log <ArrowRight size={11} className="inline" />
              </Link>{" "}
              for the transcript.
            </span>
          </li>
        </ol>
      </div>

      {/* Reuse the existing card — one source of truth for Apex controls */}
      <ApexCard />
    </div>
  );
}


function BindExistingNumberCard() {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [label, setLabel] = useState("");
  const [sourceSite, setSourceSite] = useState("");
  const [busy, setBusy] = useState(false);
  const [bound, setBound] = useState(null);
  const webhookUrl = "https://ispeedtoclose.com/api/apex/twiml/answer";

  const submit = async (e) => {
    e.preventDefault();
    if (!phoneNumber.trim()) { toast.error("Enter the phone number"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/twilio-numbers/bind-existing", {
        phone_number: phoneNumber.trim(),
        label: label.trim() || null,
        source_site: sourceSite.trim() || null,
      });
      setBound(data);
      toast.success(`${data.phone_number} bound to Apex`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bind failed");
    } finally {
      setBusy(false);
    }
  };

  const copyWebhook = () => {
    navigator.clipboard.writeText(webhookUrl);
    toast.success("Webhook URL copied");
  };

  if (bound) {
    return (
      <div className="card mb-5 bg-emerald-50 border-emerald-200" data-testid="apex-bind-success">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-full bg-emerald-500 text-white flex items-center justify-center flex-shrink-0">
            <Check size={16} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-emerald-900">
              {bound.phone_number} is bound to your Apex account.
            </div>
            <div className="text-sm text-emerald-800 mt-1">
              Next: in Twilio Console, set that number&apos;s Voice webhook to:
            </div>
            <div className="flex items-center gap-2 mt-2 bg-white border border-emerald-200 rounded px-3 py-2 text-xs">
              <code className="flex-1 text-[var(--ink)] break-all">{webhookUrl}</code>
              <button onClick={copyWebhook} className="btn-outline btn-icon-only" title="Copy"
                      data-testid="apex-bind-copy-webhook">
                <Copy size={12} />
              </button>
            </div>
            <button
              onClick={() => { setBound(null); setPhoneNumber(""); setLabel(""); setSourceSite(""); }}
              className="text-xs text-emerald-800 underline mt-2"
              data-testid="apex-bind-add-another"
            >
              Bind another number
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="card mb-5" data-testid="apex-bind-existing-card">
      <div className="label-eyebrow flex items-center gap-1.5 mb-1">
        <PhoneCall size={11} /> Bind an existing Twilio number
      </div>
      <p className="text-xs text-[var(--ink-3)] mb-3">
        Register a number you already own in your Twilio account. Reuses your
        existing Twilio credentials — no need to re-enter account SID or auth token.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label htmlFor="apex-bind-phone" className="text-[11px] uppercase tracking-widest text-[var(--ink-3)] font-bold block mb-1">
            Phone number (E.164)
          </label>
          <input
            id="apex-bind-phone"
            type="tel"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            placeholder="+18775091824"
            className="input w-full"
            data-testid="apex-bind-phone-input"
            required
          />
        </div>
        <div>
          <label htmlFor="apex-bind-label" className="text-[11px] uppercase tracking-widest text-[var(--ink-3)] font-bold block mb-1">
            Label (optional)
          </label>
          <input
            id="apex-bind-label"
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="TP Acquisitions Toll-Free"
            className="input w-full"
            data-testid="apex-bind-label-input"
            maxLength={80}
          />
        </div>
        <div>
          <label htmlFor="apex-bind-site" className="text-[11px] uppercase tracking-widest text-[var(--ink-3)] font-bold block mb-1">
            Source site tag (optional)
          </label>
          <input
            id="apex-bind-site"
            type="text"
            value={sourceSite}
            onChange={(e) => setSourceSite(e.target.value)}
            placeholder="tpacquisitions"
            className="input w-full"
            data-testid="apex-bind-site-input"
            maxLength={40}
          />
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 mt-4">
        <div className="text-[11px] text-[var(--ink-3)]">
          Source site tags enable site-aware SMS routing (multi-brand ops).
        </div>
        <button
          type="submit"
          disabled={busy}
          className="btn-money flex items-center gap-2"
          data-testid="apex-bind-submit"
        >
          {busy ? (
            <><Loader2 size={14} className="animate-spin" /> Binding…</>
          ) : (
            <><PhoneCall size={14} /> Bind number to Apex</>
          )}
        </button>
      </div>
    </form>
  );
}
